import { Fragment, useCallback, useEffect, useMemo } from 'react'

import { useMediaQuery } from '@react-hook/media-query'
import { IntlProvider } from 'react-intl'

import {
	MediaQueryProvider,
	NoOpErrorBoundary,
	RouterProvider,
	renderDefaultSnackbar,
	SnackbarProvider,
	ThemeProvider,
	UserManagementProvider,
	useLocalStorage,
	useStore,
} from '@subaio/components'
import { type Api, isPageEqual, type KeycloakWrapper, logError, type Page, type Router } from '@subaio/utils'
import { defaultBreakpointQueries } from '@subaio/webapp'
import { ContainerStatus, initializeContainer, invalidate } from '@subaio/webapp/src/containers'

import { ApiProvider } from 'lib/api'
import type { Container } from 'lib/container'
import { getContainer } from 'lib/getContainer'
import { intlFormats } from 'lib/intlFormats'
import type { Store } from 'lib/store'

import { GlobalStyle, getTheme } from 'styles'

import { Error } from 'components/Error'
import { ModalProvider } from 'components/Modal'
import { Page as PageComponent } from 'components/Page'

type Props = {
	api: Api
	store: Store
	setToken: (options?: { token: string; userGroupId: string }) => void
	router: Router
	keycloak: KeycloakWrapper
	containers: Record<string, Container>
	environment: string | null
	statuspageApi: Api
}

export const App = ({ api, store, setToken, router, keycloak, containers, environment, statuspageApi }: Props) => {
	const { page, status, data, userAuth } = useStore(store)
	const developerAuth = useStore(keycloak)
	const isMobile = useMediaQuery('only screen and (max-width: 768px)')
	const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
	const [colorScheme] = useLocalStorage<'dark' | 'light' | 'system'>('colorScheme', 'system')
	const theme = useMemo(() => {
		if (colorScheme === 'system') {
			return getTheme({
				colorScheme: prefersDarkMode ? 'dark' : 'light',
				mode: isMobile ? 'mobile' : 'desktop',
			})
		}

		return getTheme({
			colorScheme,
			mode: isMobile ? 'mobile' : 'desktop',
		})
	}, [isMobile, prefersDarkMode, colorScheme])

	const container = getContainer(containers, page, developerAuth)

	const setData = useCallback(
		(update: Record<string, any>) => {
			const { data, page: currentPage } = store.getState()
			// Abort state updates if page has changed
			if (page !== currentPage) return
			store.set({ data: { ...data, ...update } })
		},
		[store, page],
	)

	const updatePage = useCallback(
		(page: Page) => {
			const { page: currentPage } = store.getState()
			if (isPageEqual(page, currentPage, 'query')) {
				store.set({ page })
			} else {
				store.set({ page, status: ContainerStatus.Initializing, data: {} })
			}
		},
		[store],
	)

	const invalidateSelf = useCallback(
		(...invalidDependencies: Array<string>) =>
			invalidate(
				store,
				containers,
				{ statuspageApi, query: page.query, setToken, updatePage, router },
				async () => undefined,
				invalidDependencies,
			),
		[store, containers, statuspageApi, page.query, setToken, updatePage, router],
	)

	useEffect(() => {
		if (typeof container.title !== 'string') return

		const original = document.title
		document.title = `${container.title} • ${original}`

		return () => {
			document.title = original
		}
	}, [container.title])

	useEffect(() => {
		if (status !== ContainerStatus.Initializing) return

		switch (container.authentication.type) {
			case 'user': {
				initializeContainer(
					store,
					container,
					{ statuspageApi, query: page.query, setToken, updatePage, router },
					async () => undefined,
				)
				break
			}
			case 'developer': {
				if (developerAuth.status === 'uninitialized') {
					keycloak.initialize()
					return
				}
				if (developerAuth.status === 'initializing') return
				initializeContainer(
					store,
					container,
					{ statuspageApi, query: page.query, setToken, updatePage, router },
					async () => undefined,
				)
				break
			}
			case 'public': {
				initializeContainer(
					store,
					container,
					{ statuspageApi, query: page.query, setToken, updatePage, router },
					async () => undefined,
				)
				break
			}
		}
	}, [statuspageApi, store, container, status, updatePage, setToken, page, developerAuth.status, keycloak, router])

	const props = {
		...page.params,
		userAuthStatus: userAuth.status,
		developerAuthStatus: developerAuth.status,
		query: page.query,
		invalidateSelf,
		setData,
		setToken,
		statuspageApi,
		loginDeveloper: () => keycloak.login(),
	}

	return (
		<ApiProvider value={api}>
			<ThemeProvider theme={theme}>
				<GlobalStyle />
				<IntlProvider locale='en-DK' defaultLocale='en-DK' formats={intlFormats} defaultFormats={intlFormats}>
					<RouterProvider router={router} page={page} onPageChange={updatePage}>
						<MediaQueryProvider queries={defaultBreakpointQueries}>
							<ModalProvider mode='absolute'>
								<UserManagementProvider value={keycloak}>
									<PageComponent
										pageName={page.name}
										environment={environment}
										userAuth={userAuth}
										developerAuth={developerAuth}
										statuspageApi={statuspageApi}
										onLogout={() => {
											setToken(undefined)
											keycloak.logout()
										}}
									>
										<SnackbarProvider render={renderDefaultSnackbar}>
											<NoOpErrorBoundary
												onError={(error, info) => {
													logError(error, info)
													store.set({ status: ContainerStatus.Errored })
												}}
											>
												<Fragment key={page.name}>
													{status === ContainerStatus.Running && <container.Running {...data} {...props} />}
													{status === ContainerStatus.Loading && <container.Loading {...data} {...props} />}
													{status === ContainerStatus.Initializing && (
														<container.Loading {...container.defaults} {...props} />
													)}
													{status === ContainerStatus.Errored && <Error />}
												</Fragment>
											</NoOpErrorBoundary>
										</SnackbarProvider>
									</PageComponent>
								</UserManagementProvider>
							</ModalProvider>
						</MediaQueryProvider>
					</RouterProvider>
				</IntlProvider>
			</ThemeProvider>
		</ApiProvider>
	)
}
